import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { walkClaimText } from '../../../../lib/walkClaimText';
import { PowerIntentIntroStrip } from './PowerIntentIntroStrip';
import {
  summarizeIntentFeasibility,
  assessPhaseFeasibility,
  firmCapacitySiteContextFromIntent,
  RESERVE_MARGIN_PCT,
} from '../../../../lib/phaseFeasibility';
import { formatPowerBandLayer, resolveIntentPowerStack, resolveIntentPowerStackBand, sizingBasisLabel } from '../../../../lib/powerStack';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialSubsection, EditorialInstrumentPanel, EditorialCallout } from '../../report/editorial';
import { resolveSectionCallout } from '../../../../lib/reportRendering/reportSectionCallouts';
import { GenerationCapacityResilienceBlock, GenerationFuelSupplyBlock, GenerationMaxPowerBlock, GenerationScreeningEconomicsBlock } from '../../report/GenerationImpactProjectionBlocks';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';
import { buildPhaseCapabilityTimeline } from '../../../../lib/phaseCapabilityTimeline';
import {
  blockCountsFromScenarioUnits,
  resolvePromotedScenarioPhaseContingency,
} from '../../../../lib/phaseContingency';
import { PhaseFirmCapabilityChart, PhaseCriticalPathChart } from '../../report/PhaseCapabilityCharts';
import { EditorialExhibit } from '../../report/editorial';

const MONO = 'var(--report-body-font, inherit)';

/** Round to 1 decimal for compact display. */
function r1(mw: number): number {
  return Math.round(mw * 10) / 10;
}

/**
 * R-FIN-PHASE-D — Capacity-Credit & Firm Adequacy (ELCC) deep-dive section.
 *
 * Renders the AI-emitted `powerAnalysis.capacityCreditDeepDive` (optional —
 * present only when an AND-combined phase was assessable) and ALWAYS shows the
 * deterministic power-basis + firm-deliverable strip (IT → facility → MVA +
 * planning-default ELCC) so the section is never empty when the intent declares
 * a checkable combined mix.
 *
 * The ELCC / capacity-credit figures are PLANNING DEFAULTS, not a site-specific
 * ELCC study — the strip and the deep-dive both say so. Theme-aware via
 * roleColor + the editorial primitives.
 */
const LegacyCapacityCreditDeepDiveSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const deep = report.powerAnalysis?.capacityCreditDeepDive;
  const intent = report.projectIntentAtGen;
  // Deterministic feasibility roll-up (advisory; never blocks).
  const firmSummary = (() => {
    try { return summarizeIntentFeasibility(intent); }
    catch { return null; }
  })();
  const hasAssessedPhases = (firmSummary?.assessedCount ?? 0) > 0;
  const perPhase = deep?.perPhase ?? [];
  // The basis strip resolves whenever the intent has usable capacity, so the
  // section carries data even before the AI populates the deep-dive object.
  const hasPromotedScenario = !!report.generationImpactProjection?.capacityAndResilience;
  const hasData = !!deep || hasAssessedPhases || !!intent?.powerRequirementMW || hasPromotedScenario;

  // Phase-plan exhibits. Block counts come from the promoted scenario's frozen
  // units when one exists, so the N-1 series runs on DECLARED machines rather
  // than the worst-case assumption. `now` is left to default (the live clock),
  // matching `derivePhasingRisks` — the chart's "today" marker and the phasing
  // risk text therefore always read off the same clock and cannot disagree.
  const phaseTimeline = React.useMemo(
    () => {
      const solvedPhaseContingency = resolvePromotedScenarioPhaseContingency(report);
      return buildPhaseCapabilityTimeline(intent, {
        blockCounts: solvedPhaseContingency
          ? blockCountsFromScenarioUnits(report.generationImpactScenarioAtGen?.input?.units ?? [])
          : {},
        solvedPhaseContingency,
      });
    },
    [intent, report],
  );

  // K3 — dark "instrument-panel" ELCC card (the redesign's "CAPACITY CREDIT ·
  // ELCC / FIRM-DELIVERABLE 48.5 MW" panel, re-skinned to OUR theme tokens). The
  // dark surface is derived from the theme, not hardcoded. Built from the same
  // resolved power stack the intro strip uses (IT → facility → MVA), so the
  // figures match exactly; null when the stack doesn't resolve.
  const stack = (() => {
    try { return resolveIntentPowerStack(intent); }
    catch { return null; }
  })();
  const worstFirmMW = (() => {
    try {
      let worst: number | null = null;
      const feasibilityContext = firmCapacitySiteContextFromIntent(intent);
      for (const p of intent?.phases ?? []) {
        const f = assessPhaseFeasibility(p, feasibilityContext);
        if (!f) continue;
        if (worst == null || f.firmDeliverableMW < worst) worst = f.firmDeliverableMW;
      }
      return worst;
    } catch { return null; }
  })();
  // BUCKET-DERIVED fallback — an exact-only gate dropped the whole ELCC panel
  // for a range-bucket site, so the deep dive lost its power-basis anchor.
  const band = (() => {
    if (stack?.facilityMW != null) return null;
    try { return resolveIntentPowerStackBand(intent); }
    catch { return null; }
  })();
  const elccPanel = (() => {
    // Early screening already renders the declared basis and frozen modeled
    // ranges. This advanced card resolves default point PUE/PF values, which
    // must not reintroduce an unsupported facility/MVA sizing point here.
    if (report.screeningBasis?.stage === 1) return null;
    const hasFirm = typeof worstFirmMW === 'number' && Number.isFinite(worstFirmMW) && worstFirmMW > 0;
    const rows: Array<{ key: React.ReactNode; value: React.ReactNode }> = [];

    if (stack && stack.facilityMW != null) {
      if (stack.itLoadMW != null) rows.push({ key: 'IT load', value: `${r1(stack.itLoadMW)} MW` });
      rows.push({ key: `Facility ×PUE ${stack.pue}`, value: `${r1(stack.facilityMW)} MW` });
      if (stack.apparentMVA != null) {
        rows.push({ key: `Apparent ÷PF ${stack.powerFactor}`, value: `${r1(stack.apparentMVA)} MVA` });
      }
    } else if (band) {
      const fmt = (l: Parameters<typeof formatPowerBandLayer>[0]) =>
        formatPowerBandLayer(l, (n) => String(r1(n)));
      rows.push({ key: 'IT load (band)', value: fmt(band.itLoadMW) });
      rows.push({ key: `Facility ×PUE ${band.pue} (band)`, value: fmt(band.facilityMW) });
      rows.push({ key: `Apparent ÷PF ${band.powerFactor} (band)`, value: fmt(band.apparentMVA) });
      rows.push({
        key: `Sizing basis (${sizingBasisLabel(band.sizingBasisRule)})`,
        value: `${r1(band.facilityMW.sizingBasis)} MW`,
      });
    } else {
      return null;
    }

    rows.push({ key: 'Reserve margin', value: `${RESERVE_MARGIN_PCT}%` });
    return { rows, firm: hasFirm ? r1(worstFirmMW as number) : null };
  })();

  const callout = resolveSectionCallout(report, 'capacityCreditDeepDive');

  const phaseLabelStyle: React.CSSProperties = {
    fontFamily: MONO,
    fontSize: REPORT_TEXT_PX.caption,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: roleColor(theme, 'muted'),
  };

  // William QA 2026-06-28 (Img 18 "this section is empty") — when there is NO
  // capacity data (no deep-dive, no assessed phases, no intent power requirement)
  // this section previously rendered only the "requires inputs" placeholder,
  // consuming a near-blank page. Suppress it entirely instead: the TOC already
  // excludes it (the powerAnalysis.capacityCreditDeepDive field path is empty, and
  // it is unnumbered), so the body now agrees by rendering nothing — no wasted page.
  if (!hasData) return null;

  return (
    <SectionShell label={label} hasData={hasData}>
      <PowerIntentIntroStrip report={report} focus="capacity" />
      <GenerationCapacityResilienceBlock report={report} />
      <GenerationScreeningEconomicsBlock report={report} />
      <GenerationFuelSupplyBlock report={report} />
      {/* The phase-plan exhibits. Both derive from the SAME assessments that
          raise the phasing risks, so a chart can never contradict a risk row.
          Each renders only when the plan carries assessable phases. */}
      {phaseTimeline.points.length > 0 && (
        <EditorialExhibit
          theme={theme}
          span="columns"
          caption={
            <>
              Firm capability by phase, with everything running (N&ndash;0) beside what survives
              losing the largest block (N&ndash;1), against the reserve-padded requirement.
              {phaseTimeline.anyHiddenExposure
                ? ' A hatched N-1 bar marks a phase that reads adequate at N-0 and falls short at N-1.'
                : ''}
              {phaseTimeline.anyWorstCase
                ? ' Block counts are not declared in the phase plan, so N-1 assumes the worst case of one block per source.'
                : ''}
              {phaseTimeline.solvedQualificationNote
                ? ` ${phaseTimeline.solvedQualificationNote}`
                : ''}
            </>
          }
        >
          <PhaseFirmCapabilityChart theme={theme} timeline={phaseTimeline} />
        </EditorialExhibit>
      )}
      {phaseTimeline.bars.length > 0 && (
        <EditorialExhibit
          theme={theme}
          span="columns"
          caption={
            <>
              Long-lead order windows: each bar spans the latest defensible order date to the phase
              COD, on the platform&rsquo;s cited lead-time baseline. Equipment delivery only, so
              construction, commissioning, permitting and interconnection push the real order-by
              date earlier.
            </>
          }
        >
          <PhaseCriticalPathChart theme={theme} timeline={phaseTimeline} />
        </EditorialExhibit>
      )}
      {/* The upward mirror: how much the envelope could hold, beside how
          much the promoted scenario actually installs. Renders only when a
          fresh max-power study is stamped for this scenario. */}
      <GenerationMaxPowerBlock report={report} />
      {deep?.summary && (
        <EditorialSubsection theme={theme} label="Firm-capacity summary">
          {walkClaimText(deep.summary, report.references)}
        </EditorialSubsection>
      )}
      {elccPanel && (
        <div className="mt-3" style={{ maxWidth: 360 }}>
          <EditorialInstrumentPanel
            theme={theme}
            heading="Capacity Credit · ELCC"
            rows={elccPanel.rows}
            heroValue={elccPanel.firm != null ? String(elccPanel.firm) : undefined}
            heroUnit={elccPanel.firm != null ? 'MW' : undefined}
            heroLabel={elccPanel.firm != null ? 'Firm-Deliverable (ELCC)' : undefined}
            heroTone={elccPanel.firm != null ? 'positive' : 'default'}
            chip="pending"
            chipLabel="Planning Default"
            caveat={deep?.reserveMarginPct
              ? `Reserve-padded planning credit. ${deep.reserveMarginPct}. A full ELCC study is recommended.`
              : 'Planning-default credit, not a site-specific ELCC study. A full ELCC study is recommended.'}
          />
        </div>
      )}
      {perPhase.length > 0 && (
        <div className="mt-2">
          <div className="mb-1" style={phaseLabelStyle}>Per-phase firm adequacy (planning-default ELCC)</div>
          <div className="flex flex-col gap-2">
            {perPhase.map((row, idx) => (
              <div
                key={row.phaseLabel ?? idx}
                className="px-3 py-2 text-xs"
                style={{
                  borderTop: `1px solid ${roleColor(theme, 'hair')}`,
                }}
              >
                <div className="font-bold" style={{ color: roleColor(theme, 'ink') }}>
                  {row.phaseLabel ?? `Phase ${idx + 1}`}
                  {row.verdict && (
                    <span className="ml-1.5 font-normal" style={{ color: roleColor(theme, 'muted') }}>– {row.verdict}</span>
                  )}
                </div>
                <div style={{ color: roleColor(theme, 'body') }}>
                  {row.facilityLoadMW && <>Facility load {row.facilityLoadMW} · </>}
                  {row.requiredFirmMW && <>Required firm {row.requiredFirmMW} · </>}
                  {row.firmDeliverableMW && <>Firm-deliverable {row.firmDeliverableMW}</>}
                </div>
                {row.perSourceElcc && (
                  <div style={{ color: roleColor(theme, 'muted') }}>{walkClaimText(row.perSourceElcc, report.references)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* GC-W3 — the planning-default ELCC note IS the golden "ASSUMPTION · TO
          BE CONFIRMED" callout: a salmon, red-left-border named box flagging a
          value that must be verified (a full ELCC study) before final approval. */}
      <EditorialCallout
        id="capacity-credit-elcc-assumption"
        theme={theme}
        tone="assumption"
        label="Assumption · To be confirmed"
        style={{ marginTop: 12 }}
      >
        {walkClaimText(
          deep?.elccCaveat
            ?? 'Capacity-credit (ELCC) values are planning defaults drawn from resource-adequacy practice, not a site-specific ELCC study. A full ELCC study is recommended before final approval. Intermittent sources (solar/wind + a few hours of storage) carry low firm credit on a flat 24/7 load.',
          report.references,
        )}
      </EditorialCallout>
      {callout && (
        <EditorialCallout theme={theme} tone={callout.tone} label={callout.label} style={{ marginTop: 8 }}>
          {callout.body}
        </EditorialCallout>
      )}
    </SectionShell>
  );
};

/** A selected static profile has no ELCC/reserve, physical or firm authority.
 * Its report keeps the declared target open and does not render inactive v1
 * narratives or charts as the selected capacity assessment. */
export const CapacityCreditDeepDiveSection: React.FC<{ report: SiteRagReport; label: string }> = props => {
  if (!props.report.gridLedStandbyPlanningProjection) return <LegacyCapacityCreditDeepDiveSection {...props} />;
  return <SectionShell label={props.label} hasData>
    <PowerIntentIntroStrip report={props.report} focus="capacity" />
  </SectionShell>;
};
